import React from 'react';
import { Box, Chip, Link, Tooltip, Typography } from '@mui/material';
import { CreativeRow, GroupBy } from '../types';
import {
  MEDIA_LABELS,
  adsManagerUrl,
  creativeThumbnailSrc,
  isRollup,
  postUrl,
  videoUrl,
} from '../utils';
import CreativeThumbnail from './CreativeThumbnail';

const MEDIA_TINT: Record<string, { fg: string; bg: string }> = {
  video: { fg: '#4F46E5', bg: 'rgba(99, 102, 241, 0.10)' },
  static: { fg: '#475569', bg: 'rgba(100, 116, 139, 0.10)' },
  carousel: { fg: '#475569', bg: 'rgba(100, 116, 139, 0.10)' },
  unknown: { fg: '#64748B', bg: 'rgba(100, 116, 139, 0.06)' },
};

interface Props {
  row: CreativeRow;
  groupBy: GroupBy;
  onPreview: (row: CreativeRow) => void;
}

// The identity column: thumbnail, what the row is, and the way out to Ads
// Manager. In a rollup the thumbnail and name describe the highest-spending ad
// in the group, and the "N ads" chip says so rather than implying a single ad.
export default function CreativeCell({ row, groupBy, onPreview }: Props) {
  const tint = MEDIA_TINT[row.media_type] || MEDIA_TINT.unknown;
  const manageUrl = adsManagerUrl(row.account_id, row.ad_id);
  const video = videoUrl(row.video_id);
  const post = postUrl(row.post_id);

  const title =
    groupBy === 'headline'
      ? row.headline || '(no headline)'
      : groupBy === 'primary_text'
      ? row.primary_text || '(no primary text)'
      : row.ad_name || row.group_key;

  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.25 }}>
      <CreativeThumbnail
        src={creativeThumbnailSrc(row.thumb_proxy_url, row.thumbnail_url, row.image_url)}
        onClick={() => onPreview(row)}
      />

      <Box sx={{ minWidth: 0 }}>
        <Tooltip title={title} placement="top-start">
          <Typography
            variant="body2"
            onClick={() => onPreview(row)}
            sx={{
              fontWeight: 500,
              lineHeight: 1.3,
              cursor: 'pointer',
              '&:hover': { color: '#4F46E5' },
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            mt: 0.5,
          }}
        >
          <Chip
            label={MEDIA_LABELS[row.media_type]}
            size="small"
            sx={{
              height: 18,
              fontSize: 11,
              color: tint.fg,
              backgroundColor: tint.bg,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {isRollup(groupBy) && (
            <Chip
              label={`${row.ad_count} ad${row.ad_count === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
            />
          )}
          {row.effective_status && row.effective_status !== 'ACTIVE' && (
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {row.effective_status.toLowerCase()}
            </Typography>
          )}
          {manageUrl && (
            <Link
              href={manageUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ color: '#6366F1' }}
            >
              Ads Manager
            </Link>
          )}
          {post && (
            <Link
              href={post}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ color: '#6366F1' }}
            >
              Post
            </Link>
          )}
          {video && (
            <Link
              href={video}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ color: '#6366F1' }}
            >
              Video
            </Link>
          )}
        </Box>

        {row.campaign_name && (
          <Typography
            variant="caption"
            sx={{ color: '#64748B', display: 'block', mt: 0.25 }}
            noWrap
          >
            {row.campaign_name}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
